import { useExamContext } from "@features/exam/contexts/ExamContext";
import useMultipleExams from "@features/exam/contexts/useMultipleExams";
import Box from "@memorang/ui/components/Box";
import { QuickActionChips } from "@memorang/ui/components/QuickActionChips";
import type { QuickActionsChipType } from "@memorang/ui/types";
import CustomListSubheaderContainer from "components/CustomListSubheaderContainer";
import { useCallback, useMemo } from "react";
import type { StyleProp, ViewStyle } from "react-native";
import { List, RadioButton } from "react-native-paper";

type Props = {
	disabled?: boolean;
	variant?: "chips" | "radio";
	containerStyle?: StyleProp<ViewStyle>;
};

/** Mirrors the fewer-than-2-related-exams empty check `NestedExamChipsContainer` uses to render `null`, so callers can skip it entirely instead of rendering dead space. */
export const useHasNestedExamChips = () => {
	const { examWithState, relatedExams } = useMultipleExams();
	const allExamsCount =
		(examWithState && relatedExams.length ? 1 : 0) + relatedExams.length;
	return allExamsCount >= 2;
};

export const NestedExamChipsContainer = ({
	disabled,
	variant = "chips",
	containerStyle,
}: Props) => {
	const {
		currentExamContext: { examId },
		handleSwitchExamContext,
	} = useExamContext();
	const { examWithState, relatedExams, cachedExams } = useMultipleExams();
	const hasNestedExamChips = useHasNestedExamChips();

	const handleSelect = useCallback(
		(examId: string) => {
			const selectedExam = cachedExams?.find((exam) => exam.exam.id === examId);
			if (selectedExam) {
				handleSwitchExamContext(selectedExam);
			}
		},
		[cachedExams, handleSwitchExamContext],
	);
	const allExams = useMemo(
		() => [
			...(examWithState && relatedExams.length ? [examWithState] : []),
			...relatedExams,
		],
		[examWithState, relatedExams],
	);

	const nestedExamChips = useMemo<QuickActionsChipType[]>(() => {
		if (!allExams.length) {
			return [];
		}

		return allExams.map((exam) => ({
			type: exam.exam.id,
			label: exam.exam.name,
			selected: exam.exam.id === examId,
			tooltip: `Switch to ${exam.exam.name}`,
			disabled,
		}));
	}, [allExams, examId, disabled]);

	if (!hasNestedExamChips) {
		return null;
	}

	if (variant === "radio") {
		return (
			<Box style={containerStyle}>
				<CustomListSubheaderContainer enableGutters>
					Exam
				</CustomListSubheaderContainer>
				<Box marginHorizontal={-8}>
					{allExams?.map((exam) => {
						const isSelected = exam.exam.id === examId;
						return (
							<List.Item
								key={exam.exam.id}
								onPress={() => handleSelect(exam.exam.id)}
								disabled={disabled}
								style={{
									opacity: disabled ? 0.5 : 1,
									paddingVertical: 4,
								}}
								title={exam.exam.name}
								left={(p) => (
									<List.Icon
										{...p}
										icon={() => (
											<RadioButton.Android
												value={exam.exam.id}
												status={isSelected ? "checked" : "unchecked"}
												onPress={() => handleSelect(exam.exam.id)}
											/>
										)}
									/>
								)}
								// right={(p) => (
								// 	<List.Icon
								// 		{...p}
								// 		icon={() => <CustomChip>200 items</CustomChip>}
								// 	/>
								// )}
							/>
						);
					})}
				</Box>
			</Box>
		);
	}

	return (
		<Box style={containerStyle}>
			<QuickActionChips
				quickActionsChips={nestedExamChips}
				handleQuickActionPress={handleSelect}
			/>
		</Box>
	);
};
